<script>
import { onMounted, ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useModalStore } from '@/stores/modal.js';
import { useAuthenticationStore } from '@/stores/authentication.js'
import { useToastStore } from '@/stores/toast.js';
import { updateUser, updateUserPassword } from '@/lib/api/users.js';
import { uploadApi } from '@/lib/api.service.js';
import PasswordRequirements from "@/components/password_requirements.vue";
import SkeletonLoader from "@/components/ui/skeleton_loader.vue";

export default {
  name: "AccountSettings",
  components: {PasswordRequirements, SkeletonLoader},
  setup() {
    // I18n
    const { t, tm } = useI18n();

    // Stores
    const modalStore = useModalStore();
    const authenticationStore = useAuthenticationStore();
    const toastStore = useToastStore();

    // Variables
    const isLoading = ref(true);
    const email = ref(null);
    const firstName = ref(null);
    const lastName = ref(null);
    const birthday_month = ref(0);
    const birthday_day_of_month = ref(0);
    const phone = ref(null);
    const currentPassword = ref(null);
    const newPassword = ref("");
    const newPasswordConfirmation = ref("");
    const showPassword = ref(false);
    const passwordValid = ref(false);

    const file = ref(null);

    onMounted(async () => {
      setUserData();
      isLoading.value = false;
    });

    const handleFileUpload = () => {
      let url = `${import.meta.env.VITE_BACKEND_URL}/api/v2/users/${authenticationStore.currentUser.user.id}`;
      let image = file.value.files[0]

      let formData = new FormData();
      formData.append('data[attributes][photo]', image);

      uploadApi(url, formData).then((data) => {
          console.debug("File was uploaded", data)
          toastStore.success(t('settings.account_settings_section.profile_picture_updated'));
          updateUserAccount();
      }).catch((error) => {
          console.error('Upload failed:', error);
          toastStore.error(t('settings.account_settings_section.profile_picture_failed'));
      });
    }

    const setUserData = () => {
      if (authenticationStore.currentUser) {
        email.value = authenticationStore.currentUser.user.attributes.email;
        firstName.value = authenticationStore.currentUser.user.attributes.first_name;
        lastName.value = authenticationStore.currentUser.user.attributes.last_name;
        phone.value = authenticationStore.currentUser.user.attributes.cell_phone_number;
        birthday_month.value = authenticationStore.currentUser.user.attributes.birthday_month ||= 0;
        birthday_day_of_month.value = authenticationStore.currentUser.user.attributes.birthday_day_of_month ||= 0;
      }
    }

    const deleteUserAccount = async () => {
      modalStore.setModal(modalStore.modalTypeConstants.DELETE_ACCOUNT)
    }

    const updateUserAccountWithPassword =  async () => {
      let user = {}
      if (newPasswordConfirmation.value) {
        user.current_password = currentPassword.value;
        user.password = newPassword.value;
        user.password_confirmation = newPasswordConfirmation.value;
      }

      try {
        const response = await updateUserPassword(authenticationStore.currentUser.authenticatable_id, user);
        if (response) {
          authenticationStore.setUserCurrentUser(response.data);
          isLoading.value = false;
          toastStore.success(t('settings.account_settings_section.password_updated_success'));
        }
      } catch (error) {
        console.error('error', error);
        isLoading.value = false;
        toastStore.error(t('settings.account_settings_section.password_update_failed'));
      }

      newPassword.value = ""
      newPasswordConfirmation.value = ""
      currentPassword.value = ""
      showPassword.value = false;
    }

    const updateUserAccount = async () => {
      // isLoading.value = true;

      let user = {
        email: email.value,
        first_name: firstName.value,
        last_name: lastName.value,
        phone_number: phone.value,
        birthday_month: birthday_month.value,
        birthday_day_of_month: birthday_day_of_month.value
      };

      if (newPasswordConfirmation.value) {
        user.current_password = currentPassword.value;
        user.password = newPassword.value;
        user.password_confirmation = newPasswordConfirmation.value;
      }

      // TODO: Figure out how to update phone numbers properly - looks like they have their own table

      try {
        const response = await updateUser(authenticationStore.currentUser.authenticatable_id, user);
        if (response) {
          authenticationStore.setUserCurrentUser(response.data);
          isLoading.value = false;
          toastStore.success(t('settings.account_settings_section.account_updated_success'));
        }
      } catch (error) {
        console.error('error', error);
        isLoading.value = false;
        toastStore.error(t('settings.account_settings_section.account_update_failed'));
      }
    }

    const currentUserAvatar = computed(() => {
      if (!authenticationStore.currentUser || !authenticationStore.currentUser.user.attributes.photo_styles.large) {
        return null;
      } else {
        return decodeURIComponent(authenticationStore.currentUser.user.attributes.photo_styles.large);
      }
    });

    return {
      t,
      tm,
      isLoading,
      email,
      firstName,
      lastName,
      birthday_month,
      birthday_day_of_month,
      phone,
      currentPassword,
      newPassword,
      newPasswordConfirmation,
      showPassword,
      modalStore,
      file,
      updateUserAccount,
      handleFileUpload,
      deleteUserAccount,
      currentUserAvatar,
      passwordValid,
      updateUserAccountWithPassword
    }
  },
};
</script>

<template>
  <div class="max-w-6xl mx-auto p-6">
    <div v-if="!isLoading">
      <!-- Page Header -->
      <div class="mb-8">
        <h1 class="text-3xl font-bold text-gray-900 mb-2">{{ t('settings.account_settings_section.title') || 'Account Settings' }}</h1>
        <p class="text-gray-600">{{ t('settings.account_settings_section.subtitle') }}</p>
      </div>

      <!-- Profile Section -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
        <div class="p-6 border-b border-gray-200">
          <h2 class="text-xl font-semibold text-gray-900 mb-1">{{ t('settings.account_settings_section.profile_information') }}</h2>
          <p class="text-sm text-gray-600">{{ t('settings.account_settings_section.update_personal_details') }}</p>
        </div>
        
        <div class="p-6">
          <div class="flex flex-col lg:flex-row gap-8">
            <!-- Profile Picture -->
            <div class="flex flex-col items-center lg:w-1/3">
              <div class="relative mb-4">
                <div class="w-32 h-32 rounded-full overflow-hidden bg-gray-100 border-4 border-white shadow-lg">
                  <img v-if="currentUserAvatar" :src="currentUserAvatar" alt="Profile" class="w-full h-full object-cover">
                  <div v-else class="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-400 to-blue-600">
                    <svg class="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                  </div>
                </div>
                <button @click="file.click()" class="absolute -bottom-2 -right-2 w-10 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg transition-colors duration-200 flex items-center justify-center">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                  </svg>
                </button>
                <input type="file" ref="file" @change="handleFileUpload()" class="hidden" accept="image/*">
              </div>
              <p class="text-sm text-gray-500 text-center">{{ t('settings.account_settings_section.click_upload_picture') }}</p>
            </div>

            <!-- Personal Information Form -->
            <div class="flex-1">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="md:col-span-2">
                  <label for="email" class="block text-sm font-medium text-gray-700 mb-2">{{ t('settings.account_settings_section.email_label') }}</label>
                  <input type="email" id="email" v-model="email" class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 focus:bg-white" :placeholder="t('settings.account_settings_section.email_placeholder')">
                </div>
                
                <div>
                  <label for="first_name" class="block text-sm font-medium text-gray-700 mb-2">{{ t('settings.account_settings_section.first_name_label') }}</label>
                  <input type="text" id="first_name" v-model="firstName" class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 focus:bg-white" :placeholder="t('settings.account_settings_section.first_name_placeholder')">
                </div>
                
                <div>
                  <label for="last_name" class="block text-sm font-medium text-gray-700 mb-2">{{ t('settings.account_settings_section.last_name_label') }}</label>
                  <input type="text" id="last_name" v-model="lastName" class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 focus:bg-white" :placeholder="t('settings.account_settings_section.last_name_placeholder')">
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">{{ t('settings.account_settings_section.birthday_label') }}</label>
                  <div class="flex gap-3">
                    <select v-model="birthday_month" class="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                      <option :value="0" disabled>{{ t('settings.account_settings_section.birthday_placeholder.month') }}</option>
                      <option v-for="(month, index) in tm('settings.account_settings_section.birthday_month')" :key="month" :value="index + 1">{{ month }}</option>
                    </select>
                    <select v-model="birthday_day_of_month" class="w-20 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
                      <option :value="0" disabled>{{ t('settings.account_settings_section.birthday_placeholder.day_of_month') }}</option>
                      <option v-for="index in 31" :key="index" :value="index">{{ index }}</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label for="phone_number" class="block text-sm font-medium text-gray-700 mb-2">{{ t('settings.account_settings_section.phone_number_label') }}</label>
                  <input type="tel" id="phone_number" v-model="phone" class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 focus:bg-white" :placeholder="t('settings.account_settings_section.phone_number_placeholder')">
                </div>
              </div>
              

              
              <!-- Save Button -->
              <div class="mt-6">
                <button @click="updateUserAccount" class="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200">
                  {{ t('settings.account_settings_section.save_changes_button') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Password Section -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
        <div class="p-6 border-b border-gray-200">
          <h2 class="text-xl font-semibold text-gray-900 mb-1">{{ t('settings.account_settings_section.change_password') }}</h2>
          <p class="text-sm text-gray-600">{{ t('settings.account_settings_section.update_password_secure') }}</p>
        </div>
        
        <div class="p-6">
          <div class="max-w-md">
            <div class="space-y-4">
              <div class="relative">
                <label for="current_password" class="block text-sm font-medium text-gray-700 mb-2">{{ t('settings.account_settings_section.current_password_label') }}</label>
                <input v-model="currentPassword" id="current_password" :type="showPassword ? 'text' : 'password'" class="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 focus:bg-white" :placeholder="t('settings.account_settings_section.current_password_placeholder')">
                <button @click="showPassword = !showPassword" class="absolute right-3 top-9 text-gray-400 hover:text-gray-600">
                  <svg v-if="showPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"/>
                  </svg>
                  <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                  </svg>
                </button>
              </div>
              
              <div class="relative">
                <label for="new_password" class="block text-sm font-medium text-gray-700 mb-2">{{ t('settings.account_settings_section.new_password_label') }}</label>
                <input v-model="newPassword" id="new_password" :type="showPassword ? 'text' : 'password'" class="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 focus:bg-white" :placeholder="t('settings.account_settings_section.new_password_placeholder')">
                <button @click="showPassword = !showPassword" class="absolute right-3 top-9 text-gray-400 hover:text-gray-600">
                  <svg v-if="showPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"/>
                  </svg>
                  <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                  </svg>
                </button>
              </div>
              
              <div class="relative">
                <label for="new_password_confirmation" class="block text-sm font-medium text-gray-700 mb-2">{{ t('settings.account_settings_section.confirm_password_label') }}</label>
                <input v-model="newPasswordConfirmation" id="new_password_confirmation" :type="showPassword ? 'text' : 'password'" class="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 focus:bg-white" :placeholder="t('settings.account_settings_section.confirm_password_placeholder')">
                <button @click="showPassword = !showPassword" class="absolute right-3 top-9 text-gray-400 hover:text-gray-600">
                  <svg v-if="showPassword" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"/>
                  </svg>
                  <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                  </svg>
                </button>
              </div>
            </div>
            
            <PasswordRequirements class="mt-4" :password="newPassword" :password_confirmation="newPasswordConfirmation" :password_confirmation_required="true" @allValid="(value) => passwordValid = value" />
            

            
            <!-- Update Password Button -->
            <div class="mt-6">
              <button @click="updateUserAccountWithPassword" :disabled="!passwordValid" class="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors duration-200">
                {{ t('settings.account_settings_section.save_changes_button') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Danger Zone -->
      <div class="bg-white rounded-xl shadow-sm border border-red-200">
        <div class="p-6 border-b border-red-200">
          <h2 class="text-xl font-semibold text-red-900 mb-1">{{ t('settings.account_settings_section.danger_zone') }}</h2>
          <p class="text-sm text-red-600">{{ t('settings.account_settings_section.irreversible_actions') }}</p>
        </div>
        
        <div class="p-6">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-lg font-medium text-gray-900">{{ t('settings.account_settings_section.delete_account') }}</h3>
              <p class="text-sm text-gray-600 mt-1">{{ t('settings.account_settings_section.delete_account_warning') }}</p>
            </div>
            <button @click="deleteUserAccount" class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors duration-200 flex items-center">
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
              </svg>
              {{ t('settings.account_settings_section.delete_account_button') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    
    <div v-else class="flex justify-center items-center min-h-96">
      <SkeletonLoader type="form" />
    </div>
  </div>
</template>


<style lang="scss" scoped>
@import '@/assets/styles/views/settings/personal_information';
</style>
